import { onUnmounted, ref } from 'vue';
import { watchImmediate } from '@vueuse/core';
import type { Ref } from 'vue';
import { API_URL } from '@Constants';
import {
  LAST_TAB_LOGOUT_ACTIVE_WINDOW_MS,
  LAST_TAB_LOGOUT_ANIMALSFIRST_NAVIGATION_WINDOW_MS,
  LAST_TAB_LOGOUT_HEARTBEAT_MS,
  LAST_TAB_LOGOUT_KEYBOARD_RELOAD_WINDOW_MS,
  LAST_TAB_LOGOUT_NAVIGATE_RELOAD_WINDOW_MS,
  LAST_TAB_LOGOUT_PENDING_DEFERRED_KEY,
  LAST_TAB_SESSION_TAB_ID_KEY,
  LAST_TAB_SKIP_UNLOAD_LOGOUT_KEY,
  tabRegistryStorageKey,
} from '@/constants/lastTabLogout';

/**
 * NOTE :: This composable is used to handle the last-tab logout functionality.
 * `setTimeout` after `pagehide` does not run when the tab is closing (timers are dropped).
 * We call `fetch(keepalive)` synchronously from `pagehide` when no peers remain.
 *
 * Reload detection uses time windows only (no sticky booleans): a real reload pairs Navigation
 * API `navigate(reload)` with `pagehide` within ~1s. `pagehide` with `persisted` must clear
 * hints so bfcache does not leak stale reload state into a later tab close.
 *
 * Firefox specifics: credentialed `GET /logout?expiredSession=1` with `fetch(..., { keepalive: true })` (POST/beacon
 * and hidden iframes are unreliable cross-origin on unload). We also attach `unload` as a second
 * chance for last-tab work, with a sessionStorage flag when `pagehide` skipped logout for reload.
 * `pageshow` (persisted) re-registers after bfcache. `persisted` on `pagehide` may mean real close in
 * Gecko (Firefox's browser engine), so we still run last-tab logic there unlike other engines.
 */

type PendingLogout = { tabId: string; timestamp: number };
type TabRegistry = Record<string, number>;

let navigateReloadAtMs = 0; // NOTE :: Timestamp of last Navigation API `navigate` with type reload; 0 if none.
let keyboardReloadAtMs = 0; // NOTE :: Timestamp of last F5 / Ctrl+R keydown; 0 if none.
let animalsFirstNavigateAtMs = 0; // NOTE :: Timestamp of navigation intent to another Animals First host.
let globalReloadListenersInstalled = false;

// GETTERS
function isFirefox(): boolean {
  // NOTE :: Firefox Gecko keepalive fetch often never reaches the server on tab close; `persisted` can be true on real closes.
  return typeof navigator !== 'undefined' && /\bFirefox\//i.test(navigator.userAgent);
}

function getOrCreateTabId(): string {
  let id = sessionStorage.getItem(LAST_TAB_SESSION_TAB_ID_KEY);

  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(LAST_TAB_SESSION_TAB_ID_KEY, id);
  }

  return id;
}

function getShouldSkipLastTabLogoutForReload(nowMs: number): boolean {
  const navigateRecent =
    navigateReloadAtMs > 0 && nowMs - navigateReloadAtMs < LAST_TAB_LOGOUT_NAVIGATE_RELOAD_WINDOW_MS;

  const keyboardRecent =
    keyboardReloadAtMs > 0 && nowMs - keyboardReloadAtMs < LAST_TAB_LOGOUT_KEYBOARD_RELOAD_WINDOW_MS;

  const animalsFirstNavigationRecent =
    animalsFirstNavigateAtMs > 0 &&
    nowMs - animalsFirstNavigateAtMs < LAST_TAB_LOGOUT_ANIMALSFIRST_NAVIGATION_WINDOW_MS;

  return navigateRecent || keyboardRecent || animalsFirstNavigationRecent;
}

function getXsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/);

  return match ? decodeURIComponent(match[1]) : null;
}

function getRegistry(userId: string): TabRegistry {
  const raw = localStorage.getItem(tabRegistryStorageKey(userId));
  if (!raw) return {};

  const parsed = JSON.parse(raw) as unknown;
  const notValidFormat = !parsed || typeof parsed !== 'object';
  if (notValidFormat) return {};

  return parsed as TabRegistry;
}

function getNumberOfActiveTabs(userId: string, now = Date.now()): number {
  const registry = getRegistry(userId);
  let numberOfActiveTabs = 0;

  for (const [, seen] of Object.entries(registry)) {
    if (typeof seen !== 'number') continue;

    if (now - seen < LAST_TAB_LOGOUT_ACTIVE_WINDOW_MS) numberOfActiveTabs++;
  }

  return numberOfActiveTabs;
}

// MUTATORS
function resetReloadHints() {
  navigateReloadAtMs = 0;
  keyboardReloadAtMs = 0;
  animalsFirstNavigateAtMs = 0;
}

function isAnimalsFirstHost(hostname: string): boolean {
  return /(^|\.)animalsfirst(\.|$)/i.test(hostname);
}

function markAnimalsFirstNavigationIntent(url: URL): void {
  const currentHost = window.location.hostname;
  const targetHost = url.hostname;
  const isSameHost = currentHost === targetHost;
  if (isSameHost) return;
  if (!isAnimalsFirstHost(targetHost)) return;

  animalsFirstNavigateAtMs = Date.now();
}

function installAnimalsFirstNavigationIntentListener() {
  document.addEventListener('click', onDocumentClick, true);
}

function installKeyboardReloadListener() {
  window.addEventListener(
    'keydown',
    ({ ctrlKey, metaKey, key }: KeyboardEvent) => {
      if (key === 'F5') {
        keyboardReloadAtMs = Date.now();
        return;
      }

      if ((key === 'r' || key === 'R') && (ctrlKey || metaKey)) {
        keyboardReloadAtMs = Date.now();
      }
    },
    true
  );
}

function setRegistry(userId: string, registry: TabRegistry) {
  const key = tabRegistryStorageKey(userId);

  const isEmpty = Object.keys(registry).length === 0;
  if (isEmpty) {
    localStorage.removeItem(key);
    return;
  }

  localStorage.setItem(key, JSON.stringify(registry));
}

function setTabHeartbeat(userId: string, tabId: string) {
  const registry = getRegistry(userId);
  const now = Date.now();

  for (const [key, seen] of Object.entries(registry)) {
    const isNotValidType = typeof seen !== 'number';
    const isInactive = isNotValidType || now - seen >= LAST_TAB_LOGOUT_ACTIVE_WINDOW_MS;

    if (isInactive) delete registry[key];
  }

  registry[tabId] = now;

  setRegistry(userId, registry);
}

function deleteTabFromRegistry(userId: string, tabId: string) {
  const registry = getRegistry(userId);
  delete registry[tabId];

  setRegistry(userId, registry);
}

function removeUserTabRegistry(userId: string) {
  // NOTE :: Clears the shared tab registry and any deferred logout record for a user (e.g. explicit menu logout).
  if (!userId) return;

  localStorage.removeItem(tabRegistryStorageKey(userId));
  localStorage.removeItem(pendingLogoutStorageKey(userId));
}

function pendingLogoutStorageKey(userId: string): string {
  return `${LAST_TAB_LOGOUT_PENDING_DEFERRED_KEY}:${userId}`;
}

function writePendingLogout(userId: string, tabId: string) {
  const record: PendingLogout = { tabId, timestamp: Date.now() };

  localStorage.setItem(pendingLogoutStorageKey(userId), JSON.stringify(record));
}

function readPendingLogout(userId: string): PendingLogout | null {
  const raw = localStorage.getItem(pendingLogoutStorageKey(userId));
  if (!raw) return null;

  const parsed = JSON.parse(raw) as unknown;
  if (!parsed || typeof parsed !== 'object') return null;

  const record = parsed as Record<string, unknown>;
  if (typeof record.tabId !== 'string' || typeof record.timestamp !== 'number') return null;

  return { tabId: record.tabId, timestamp: record.timestamp };
}

function clearPendingLogout(userId: string) {
  localStorage.removeItem(pendingLogoutStorageKey(userId));
}

// EVENTS
function onDocumentClick(event: MouseEvent) {
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const targetNode = event.target;
  if (!(targetNode instanceof Element)) return;

  const anchor = targetNode.closest('a[href]');
  if (!anchor) return;

  const targetAttr = anchor.getAttribute('target');
  const opensNewContext = targetAttr && targetAttr !== '_self';
  if (opensNewContext) return;

  const href = anchor.getAttribute('href');
  if (!href) return;

  const validPrefixes = ['#', 'javascript:', 'mailto:', 'tel:'];
  if (validPrefixes.some((prefix) => href.startsWith(prefix))) return;

  const targetUrl = new URL(href, window.location.href);
  markAnimalsFirstNavigationIntent(targetUrl);
}

function onComposableMount() {
  if (globalReloadListenersInstalled || typeof window === 'undefined') return;

  globalReloadListenersInstalled = true;

  const navigation = (window as unknown as { navigation?: EventTarget }).navigation;
  const hasValidNavigationApi = navigation && typeof navigation.addEventListener === 'function';

  if (hasValidNavigationApi) {
    navigation.addEventListener('navigate', (event: Event) => {
      const navigationEvent = event as Event & { navigationType?: string; type?: string };
      const navigationType = navigationEvent.navigationType ?? navigationEvent.type;

      if (navigationType !== 'reload') {
        return;
      }

      navigateReloadAtMs = Date.now();
    });
  }

  installKeyboardReloadListener();
  installAnimalsFirstNavigationIntentListener();
}

function onPostLogoutKeepalive() {
  const token = getXsrfToken();
  const urlApi = `${API_URL}/logout-api`;
  const urlGet = `${API_URL}/logout?expiredSession=1`;

  if (isFirefox()) {
    void fetch(urlGet, { method: 'GET', keepalive: true, credentials: 'include', mode: 'cors', cache: 'no-store' });
    return;
  }

  if (!token) return;

  fetch(urlApi, {
    method: 'POST',
    keepalive: true,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-XSRF-TOKEN': token,
    },
    body: '{}',
  });
}

// NOTE :: Trigger the composable once the module loads
const isBrowserReady = typeof window !== 'undefined';
if (isBrowserReady) onComposableMount();

/**
 * NOTE
 * When the authenticated shell is active, registers this tab in localStorage and logs out
 * server-side when this tab hides and no other tab is active (`POST /logout-api`, or on Firefox
 * keepalive `GET /logout?expiredSession=1` plus a Gecko `unload` fallback).
 */
export default function useLastTabLogout(enabled: Ref<boolean>, userIdRef: Ref<string | null>) {
  const tabIdRef = ref<string | null>(null);
  const lastRegistryUserId = ref<string | null>(null); // NOTE :: User id for which this tab is registered in localStorage (stable across `userId` ref timing).
  const heartbeatTimer = ref<ReturnType<typeof setInterval> | null>(null);

  watchImmediate([enabled, userIdRef], ([isEnabled, userId]) => onSessionStateUpdate(isEnabled, userId));

  onUnmounted(onStop);

  // HELPERS
  function clearHeartbeat() {
    if (!heartbeatTimer.value) return;

    clearInterval(heartbeatTimer.value);
    heartbeatTimer.value = null;
  }

  // EVENTS

  function onSessionStateUpdate(isEnabled: boolean, userId: string | null) {
    onStop();

    if (isEnabled && userId) onStart();
  }

  function onPageHide(event: Event) {
    const pageEvent = event as PageTransitionEvent;
    if (pageEvent.persisted) {
      resetReloadHints();
      // NOTE :: Firefox may set `persisted` on tab close; still run last-tab logout. Other engines: bfcache only.
      if (!isFirefox()) return;
    }

    const userId = lastRegistryUserId.value;
    const tabId = tabIdRef.value;
    if (!userId || !tabId) return;

    const nowMs = Date.now();
    const skipLogoutForReload = getShouldSkipLastTabLogoutForReload(nowMs);
    resetReloadHints();

    deleteTabFromRegistry(userId, tabId);

    if (skipLogoutForReload) {
      sessionStorage.setItem(LAST_TAB_SKIP_UNLOAD_LOGOUT_KEY, '1');
      return;
    }

    if (getNumberOfActiveTabs(userId) > 0) {
      sessionStorage.removeItem(LAST_TAB_SKIP_UNLOAD_LOGOUT_KEY);
      return;
    }

    // NOTE :: Deferred logout: write intent to localStorage and confirm on the next page load.
    // A surviving same tabId in sessionStorage (reload, back/forward, or address-bar same-origin nav) clears the intent.
    // On true close the next load has a fresh tabId → logout fires then, instead of via keepalive here.
    // Block the Firefox `unload` fallback so it does not fire the logout we intentionally deferred.
    writePendingLogout(userId, tabId);
    sessionStorage.setItem(LAST_TAB_SKIP_UNLOAD_LOGOUT_KEY, '1');
  }

  // NOTE :: Firefox duplicate `pagehide` teardown; some Gecko builds defer or drop unload-time work without this.
  function onUnload() {
    if (sessionStorage.getItem(LAST_TAB_SKIP_UNLOAD_LOGOUT_KEY) === '1') {
      sessionStorage.removeItem(LAST_TAB_SKIP_UNLOAD_LOGOUT_KEY);
      return;
    }

    const userId = lastRegistryUserId.value;
    const tabId = tabIdRef.value;
    if (!userId || !tabId) return;

    deleteTabFromRegistry(userId, tabId);
    if (getNumberOfActiveTabs(userId) === 0) onPostLogoutKeepalive();
  }

  // NOTE :: After bfcache restore we must re-register (Firefox runs last-tab logic on some `persisted` pagehides).
  function onPageShow(event: Event) {
    const pageEvent = event as PageTransitionEvent;
    if (!pageEvent.persisted) return;

    const userId = lastRegistryUserId.value;
    const tabId = tabIdRef.value;
    if (!userId || !tabId) return;

    const pendingLogout = readPendingLogout(userId);
    if (pendingLogout?.tabId === tabId) clearPendingLogout(userId);

    setTabHeartbeat(userId, tabId);
  }

  function onStart() {
    const activeUserId = userIdRef.value;
    if (!activeUserId) return;

    tabIdRef.value = getOrCreateTabId();

    const pendingLogout = readPendingLogout(activeUserId);
    if (pendingLogout) {
      clearPendingLogout(activeUserId);

      // NOTE :: A surviving sessionStorage tab id proves this tab did not close: reload, back/forward, and
      // address-bar / pasted same-origin navigation all keep it. A true close (or a cross-origin navigation)
      // yields a fresh tab id. So `isSameTab` alone cancels the deferred logout — navigation type is not
      // reliable here, since an address-bar navigation reports `navigate`, not `reload`/`back_forward`.
      const isSameTab = pendingLogout.tabId === tabIdRef.value;

      if (!isSameTab) {
        onPostLogoutKeepalive();
        return;
      }
    }

    lastRegistryUserId.value = activeUserId;
    setTabHeartbeat(activeUserId, tabIdRef.value);

    clearHeartbeat();

    const RANDOM_OFFSET_MS = Math.floor(Math.random() * 5000);
    const INTERVAL_MS = LAST_TAB_LOGOUT_HEARTBEAT_MS + RANDOM_OFFSET_MS;

    heartbeatTimer.value = setInterval(() => {
      const userId = lastRegistryUserId.value;
      const tabId = tabIdRef.value;
      if (!userId || !tabId) return;

      setTabHeartbeat(userId, tabId);
    }, INTERVAL_MS);

    window.addEventListener('pagehide', onPageHide as EventListener);
    window.addEventListener('pageshow', onPageShow as EventListener);

    if (isFirefox()) window.addEventListener('unload', onUnload);
  }

  function onStop() {
    clearHeartbeat();

    window.removeEventListener('pagehide', onPageHide as EventListener);
    window.removeEventListener('pageshow', onPageShow as EventListener);
    window.removeEventListener('unload', onUnload);

    const registeredUserId = lastRegistryUserId.value;
    const tabId = tabIdRef.value;
    if (registeredUserId && tabId) deleteTabFromRegistry(registeredUserId, tabId);
    lastRegistryUserId.value = null;
  }
}

export { removeUserTabRegistry };
